import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { isAdmin, isLocalAdmin, getUserRole, isGlobalAdmin } from '../lib/auth';
import { logUserAction } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireLocalAdmin?: boolean;
  requireGlobalAdmin?: boolean;
}

export default function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireLocalAdmin = false,
  requireGlobalAdmin = false
}: ProtectedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setIsAuthenticated(false);
          setLoading(false);
          return;
        }

        setIsAuthenticated(true);

        // Log the access attempt
        await logUserAction('route_access', {
          path: location.pathname,
          requiresAuth: true,
          requiresAdmin: requireAdmin,
          requiresLocalAdmin: requireLocalAdmin,
          requiresGlobalAdmin: requireGlobalAdmin
        });

        if (requireAdmin || requireLocalAdmin || requireGlobalAdmin) {
          let authorized = false;
          
          if (requireGlobalAdmin) {
            // Apenas admin global pode acessar
            authorized = await isGlobalAdmin();
          } else if (requireLocalAdmin) {
            // Apenas admin local pode acessar
            authorized = await isLocalAdmin();
          } else if (requireAdmin) {
            // Qualquer tipo de admin pode acessar
            authorized = await isAdmin();
          }
          
          setIsAuthorized(authorized);
        } else {
          setIsAuthorized(true);
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        setIsAuthenticated(false);
        setIsAuthorized(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [requireAdmin, requireLocalAdmin, requireGlobalAdmin, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-teal"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ returnTo: location.pathname }} replace />;
  }

  if (!isAuthorized) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}